import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, ArrowLeft, Mail } from "lucide-react";

/**
 * Public forgot-password page — collects an email, POSTs to
 * /auth/forgot-password, shows a generic success message either way (backend
 * returns the same response whether or not the email exists — anti-enumeration).
 */
export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to login
        </Link>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8" data-testid="forgot-password-card">
          <div className="w-11 h-11 rounded-xl bg-[#0F172A] text-white flex items-center justify-center mb-4">
            <Mail className="w-5 h-5" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Reset your password</h1>
          {sent ? (
            <div className="mt-4 space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                If an account exists with <strong>{email}</strong>, we've sent a password-reset link. It expires in 2 hours.
              </p>
              <p className="text-xs text-slate-500">Didn't get it? Check spam, or try again in a minute.</p>
              <Button onClick={() => nav("/login")} className="w-full bg-[#0F172A] hover:bg-black rounded-xl h-11 font-bold">
                Back to login
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">Enter your account email and we'll send you a reset link.</p>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
                required
                data-testid="forgot-email-input"
                className="h-11 rounded-xl"
              />
              <Button type="submit" disabled={sending || !email} data-testid="forgot-submit-btn" className="w-full bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-11 font-bold disabled:opacity-40">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
